import { useState, useEffect } from "react";
import { ShoppingBag, Loader2, CreditCard, Download, CheckCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import API from "@/lib/api";

export default function Marketplace() {
  const [packages, setPackages] = useState([]);
  const [purchases, setPurchases] = useState({});
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState({});
  const [tab, setTab] = useState("browse");
  const [myPurchases, setMyPurchases] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [pkgsRes, myRes, walletRes] = await Promise.all([
        API.get("/marketplace"),
        API.get("/marketplace/purchases/mine"),
        API.get("/wallet/balance"),
      ]);
      setPackages(pkgsRes.data.packages || []);
      setMyPurchases(myRes.data.purchases || []);
      setWalletBalance(walletRes.data?.balance || 0);
      // Build purchase map for quick lookup
      const pm = {};
      (myRes.data.purchases || []).forEach(p => { pm[p.package_id] = true; });
      setPurchases(pm);
    } catch {
      toast.error("Failed to load marketplace");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg) => {
    if (walletBalance < pkg.price_credits) {
      toast.error(`Insufficient credits. Need ${pkg.price_credits}, have ${walletBalance}. Please top up.`);
      return;
    }
    if (!window.confirm(`Purchase "${pkg.title}" for ${pkg.price_credits} credits? Contacts will be added to your phonebook.`)) return;
    setPurchasing(p => ({ ...p, [pkg._id]: true }));
    try {
      const res = await API.post(`/marketplace/${pkg._id}/purchase`);
      toast.success(res.data.message || "Purchase successful!");
      setPurchases(p => ({ ...p, [pkg._id]: true }));
      const imported = res.data.contacts_imported;
      if (imported > 0) toast.success(`${imported} contacts added to your phonebook`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    } finally {
      setPurchasing(p => ({ ...p, [pkg._id]: false }));
    }
  };

  const handleDownload = async (pkg) => {
    try {
      const res = await API.get(`/marketplace/${pkg._id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = pkg.original_filename || `${pkg.title.replace(/\s+/g, "_")}_contacts.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={24} className="animate-spin text-[#0c1e41]" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Contact Marketplace</h1>
          <p className="text-sm text-slate-500 mt-1">Purchase verified contact lists — paid with your wallet credits</p>
        </div>
        <div className="flex items-center gap-2 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl px-4 py-2">
          <CreditCard size={16} className="text-[#0c1e41]" />
          <span className="text-xs text-slate-600">Balance:</span>
          <span className="text-sm font-bold text-[#0c1e41]" data-testid="marketplace-wallet-balance">{walletBalance.toLocaleString()} credits</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[{ id: "browse", label: "Browse Packages" }, { id: "my", label: `My Purchases (${myPurchases.length})` }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            data-testid={`marketplace-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Browse */}
      {tab === "browse" && (
        packages.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
            <ShoppingBag size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No packages available yet</p>
            <p className="text-slate-400 text-sm mt-1">Check back soon — the admin publishes new contact packages regularly</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map(pkg => {
              const purchased = purchases[pkg._id];
              return (
                <div key={pkg._id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`package-card-${pkg._id}`}>
                  {/* Thumbnail */}
                  <div className="h-36 bg-gradient-to-br from-[#eef1f8] to-[#dce5f5] flex items-center justify-center relative overflow-hidden">
                    {pkg.thumbnail_path ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/${pkg._id}/thumbnail`}
                        alt={pkg.title}
                        className="w-full h-full object-cover"
                        onError={e => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <ShoppingBag size={40} className="text-[#0c1e41]/30" />
                    )}
                    {purchased && (
                      <div className="absolute top-2 right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold">
                        <CheckCircle size={11} /> Purchased
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2">{pkg.title}</h3>
                    {pkg.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{pkg.description}</p>}
                    <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Users size={12} /> {(pkg.total_contacts || 0).toLocaleString()} contacts</span>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <div>
                        <span className="text-lg font-extrabold text-[#0c1e41]">{pkg.price_credits}</span>
                        <span className="text-xs text-slate-500 ml-1">credits</span>
                      </div>
                      {purchased ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(pkg)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8 text-xs"
                          data-testid={`download-btn-${pkg._id}`}
                        >
                          <Download size={13} className="mr-1.5" /> Download
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handlePurchase(pkg)}
                          disabled={purchasing[pkg._id]}
                          className="bg-[#0c1e41] hover:bg-[#091629] text-white h-8 text-xs shadow-sm"
                          data-testid={`buy-btn-${pkg._id}`}
                        >
                          {purchasing[pkg._id] ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
                          Buy Now
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* My Purchases */}
      {tab === "my" && (
        myPurchases.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
            <ShoppingBag size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No purchases yet</p>
            <p className="text-slate-400 text-sm mt-1">Browse available packages and make your first purchase</p>
          </div>
        ) : (
          <div className="space-y-3">
            {myPurchases.map(p => {
              const pkg = packages.find(pk => pk._id === p.package_id) || { _id: p.package_id, title: p.package_title, original_filename: null };
              return (
                <div key={p._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center justify-between gap-4" data-testid={`purchase-row-${p._id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                      <CheckCircle size={18} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{p.package_title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Paid {p.credits_paid} credits · {new Date(p.purchased_at).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(pkg)}
                    className="border-[#0c1e41] text-[#0c1e41] hover:bg-[#eef1f8] h-8 text-xs shrink-0"
                    data-testid={`my-download-btn-${p._id}`}
                  >
                    <Download size={13} className="mr-1.5" /> Download
                  </Button>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
